// Single source of truth for every component that is code-split with `defineAsyncComponent`.
//
// A component belongs here (instead of a plain `import`) when it is both gated behind a
// conditional (v-if, empty-state branch, modal open flag, etc.) AND carries real weight — a
// heavy third-party dependency, or enough internal complexity that most page loads shouldn't
// pay for it. Consumers just do `import { X } from '@/async-components'` — declaring the same
// `defineAsyncComponent(() => import(...))` in every consuming file duplicated boilerplate and,
// for a couple of components, a non-obvious TypeScript workaround (see FormMultiselect below).
//
// Note: this is unrelated to src/router/routes.ts's `lazyLoadView` — that's route-level code
// splitting with its own loading/timeout UI, not component-level conditional lazy-loading.
import { type Component, defineAsyncComponent } from 'vue';

// ---------------------------------------------------------------------------
// App shell
// ---------------------------------------------------------------------------
export const CookieModal = defineAsyncComponent(() => import('@/components/misc/CookieModal.vue'));
export const WalkthroughGeneric = defineAsyncComponent(() => import('@/components/shared/WalkthroughGeneric.vue'));
export const IdleWarningModal = defineAsyncComponent(() => import('@/components/IdleWarningModal.vue'));
export const AIAssistantBotButton = defineAsyncComponent(() => import('@/components/ai/AIAssistantBotButton.vue'));
export const AIAssistantBot = defineAsyncComponent(() => import('@/components/ai/AIAssistantBot.vue'));

// ---------------------------------------------------------------------------
// Form field types (used by FormBuilderDynamic and a handful of direct consumers)
// ---------------------------------------------------------------------------
export const FormRichText = defineAsyncComponent(() => import('@/components/forms/FormRichText.vue'));
export const FormFormula = defineAsyncComponent(() => import('@/components/forms/FormFormula.vue'));
export const FormTreeselect = defineAsyncComponent(() => import('@/components/forms/FormTreeselect.vue'));
export const FormDateRangePicker = defineAsyncComponent(() => import('@/components/forms/FormDateRangePicker.vue'));
export const FormDatePickerNew = defineAsyncComponent(() => import('@/components/forms/FormDatePickerNew.vue'));
export const FormMonthRangePicker = defineAsyncComponent(() => import('@/components/forms/FormMonthRangePicker.vue'));
export const FormSelectNew = defineAsyncComponent(() => import('@/components/forms/FormSelectNew.vue'));
export const FormMultiselectNew = defineAsyncComponent(() => import('@/components/forms/FormMultiselectNew.vue'));
export const FormAsset = defineAsyncComponent(() => import('@/components/forms/components/file/FormAsset.vue'));
export const GenerateImage = defineAsyncComponent(() => import('@/components/forms/components/file/GenerateImage.vue'));
// FormArray statically imports vuedraggable (~97kb), so it's split out the same way as the other
// field types below — otherwise every FormBuilderDynamic consumer pays for drag-and-drop even on
// forms with no array-type field.
export const FormArray = defineAsyncComponent(() => import('@/components/forms/FormArray.vue'));

// FormMultiselect has TranslationKey-typed props whose literal union, combined with
// defineAsyncComponent's own generics, exceeds TS's union-complexity limit (TS2590). Casting the
// inner import() promise to a plain `Component` shape before defineAsyncComponent's generics see
// it avoids the error; the tradeoff is FormMultiselect loses specific prop type-checking at its
// call sites (same tradeoff already accepted at the two sites this was originally solved for).
export const FormMultiselect = defineAsyncComponent(
    () => import('@/components/forms/FormMultiselect.vue') as unknown as Promise<{ default: Component }>,
);

// ---------------------------------------------------------------------------
// Admin — departments / users / competencies
// ---------------------------------------------------------------------------
export const RoleForm = defineAsyncComponent(() => import('@/components/admin/departments/RoleForm.vue'));
export const DepartmentRoleSelect = defineAsyncComponent(
    () => import('@/components/admin/users/DepartmentRoleSelect.vue'),
);
export const DepartmentRoleTreeSelection = defineAsyncComponent(
    () => import('@/components/admin/departments/DepartmentRoleTreeSelection.vue'),
);
export const CompetencyClustersTable = defineAsyncComponent(
    () => import('@/components/admin/competencies/CompetencyClustersTable.vue'),
);
export const CompetencyForm = defineAsyncComponent(() => import('@/components/admin/competencies/CompetencyForm.vue'));
export const CompetencyClusterForm = defineAsyncComponent(
    () => import('@/components/admin/competencies/CompetencyClusterForm.vue'),
);
export const ImportCompetenciesForm = defineAsyncComponent(
    () => import('@/components/admin/competencies/ImportCompetenciesForm.vue'),
);
export const AllCompetencyClusters = defineAsyncComponent(
    () => import('@/components/admin/competencies/AllCompetencyClusters.vue'),
);
export const AllCompetencies = defineAsyncComponent(
    () => import('@/components/admin/competencies/AllCompetencies.vue'),
);

// ---------------------------------------------------------------------------
// Charts (vue-chartjs / apexcharts)
// ---------------------------------------------------------------------------
// vue-chartjs exports Bar/Line/Radar/Doughnut as named exports, not a default export, so the
// loader resolves the module and picks the named component off it. Chart.js itself (~400kb) used
// to be registered eagerly at app boot (see main.ts history), which forced it into the entry
// bundle on every page regardless of whether a chart was ever rendered. It's now registered, via
// initializeCharts(), only when one of these components is actually requested.
function loadVueChartjsComponent<K extends 'Bar' | 'Line' | 'Radar' | 'Doughnut'>(name: K) {
    return Promise.all([import('vue-chartjs'), import('@/utils/charts-setup')]).then(([vueChartjs, chartsSetup]) => {
        chartsSetup.initializeCharts();
        return vueChartjs[name];
    });
}

export const Bar = defineAsyncComponent(() => loadVueChartjsComponent('Bar'));
export const Line = defineAsyncComponent(() => loadVueChartjsComponent('Line'));
export const Radar = defineAsyncComponent(() => loadVueChartjsComponent('Radar'));
export const Doughnut = defineAsyncComponent(() => loadVueChartjsComponent('Doughnut'));
export const HeatmapChart = defineAsyncComponent(() => import('@/views/admin/reporting/HeatmapChart.vue'));
export const TeamSkillProgressChart = defineAsyncComponent(
    () => import('@/components/admin/reports/TeamSkillProgressChart.vue'),
);
export const TeamRoleReadinessChart = defineAsyncComponent(
    () => import('@/components/admin/reports/TeamRoleReadinessChart.vue'),
);
export const RoleReadinessChart = defineAsyncComponent(() => import('@/components/user/RoleReadinessChart.vue'));
export const UserRegistrationChart = defineAsyncComponent(
    () => import('@/components/super-admin/dashboard/UserRegistrationChart.vue'),
);
export const CourseCompletionTimeChart = defineAsyncComponent(
    () => import('@/views/admin/reporting/CourseCompletionTimeChart.vue'),
);
export const PassFailDistributionChart = defineAsyncComponent(
    () => import('@/views/admin/reporting/PassFailDistributionChart.vue'),
);
export const AiAssistantUsageTrendChart = defineAsyncComponent(
    () => import('@/views/admin/reporting/AiAssistantUsageTrendChart.vue'),
);
export const ActivityTimeUsageTrendChart = defineAsyncComponent(
    () => import('@/views/admin/reporting/ActivityTimeUsageTrendChart.vue'),
);
export const EngagementTimeDistributionChart = defineAsyncComponent(
    () => import('@/views/admin/reporting/EngagementTimeDistributionChart.vue'),
);
export const UserRetentionChart = defineAsyncComponent(() => import('@/views/admin/reporting/UserRetentionChart.vue'));

// ---------------------------------------------------------------------------
// Reporting
// ---------------------------------------------------------------------------
export const ReportScopeFilter = defineAsyncComponent(() => import('@/views/admin/reporting/ReportScopeFilter.vue'));

// ---------------------------------------------------------------------------
// Simulation / decisions
// ---------------------------------------------------------------------------
export const SliderDecision = defineAsyncComponent(
    () => import('@/components/simulation/decisions/participants/SliderDecision.vue'),
);
export const ChooseAndMapDecision = defineAsyncComponent(
    () => import('@/components/simulation/decisions/participants/ChooseAndMapDecision.vue'),
);
export const MapDecision = defineAsyncComponent(
    () => import('@/components/simulation/decisions/participants/MapDecision.vue'),
);
export const AssignmentDecision = defineAsyncComponent(
    () => import('@/components/simulation/decisions/participants/AssignmentDecision.vue'),
);
export const ResourceAllocationDecision = defineAsyncComponent(
    () => import('@/components/simulation/decisions/participants/ResourceAllocationDecision.vue'),
);
export const TableDecision = defineAsyncComponent(
    () => import('@/components/simulation/decisions/participants/TableDecision.vue'),
);
export const AIConversationDecision = defineAsyncComponent(
    () => import('@/components/simulation/decisions/participants/AIConversationDecision.vue'),
);
// AIConversationTelephone (voice mode) alone pulls in @openai/agents/realtime + zod (~1MB uncompressed)
// so it's split out from AIConversationDecision separately —
// AI-conversation decisions in text mode shouldn't pay for the realtime voice SDK.
export const AIConversationTelephone = defineAsyncComponent(
    () => import('@/components/simulation/decisions/participants/AIConversationTelephone.vue'),
);
export const InfoSlidesModal = defineAsyncComponent(() => import('@/components/simulation/InfoSlidesModal.vue'));
export const Walkthrough = defineAsyncComponent(() => import('@/components/simulation/Walkthrough.vue'));
export const HelpModal = defineAsyncComponent(() => import('@/components/simulation/HelpModal.vue'));
export const DebugPanel = defineAsyncComponent(() => import('@/components/DebugPanel.vue'));

// ---------------------------------------------------------------------------
// Licensing / dashboard
// ---------------------------------------------------------------------------
export const LicenseExpiryBanner = defineAsyncComponent(() => import('@/components/licensing/LicenseExpiryBanner.vue'));
export const RequestExtensionModal = defineAsyncComponent(
    () => import('@/components/licensing/RequestExtensionModal.vue'),
);
export const OrganizationOnboardingCard = defineAsyncComponent(
    () => import('@/components/dashboard/OrganizationOnboardingCard.vue'),
);

// ---------------------------------------------------------------------------
// Misc shared
// ---------------------------------------------------------------------------
export const CardTable = defineAsyncComponent(() => import('@/components/shared/CardTable.vue'));
export const PlatformFeedback = defineAsyncComponent(
    () => import('@/components/shared/platform-feedback/PlatformFeedback.vue'),
);

// ---------------------------------------------------------------------------
// Editor — decision/element forms and drag-and-drop
// ---------------------------------------------------------------------------
export const Draggable = defineAsyncComponent(() => import('vuedraggable'));
export const ElementSelector = defineAsyncComponent(() => import('@/editor/core/components/ElementSelector.vue'));
export const FormulaForm = defineAsyncComponent(() => import('@/editor/core/components/FormulaForm.vue'));
export const QuestionForm = defineAsyncComponent(() => import('@/editor/core/components/forms/QuestionForm.vue'));
export const CourseTaskForm = defineAsyncComponent(() => import('@/editor/core/components/forms/CourseTaskForm.vue'));
export const TextInfoForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/static-elements-configs/TextInfoForm.vue'),
);
export const ImageForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/static-elements-configs/ImageForm.vue'),
);
export const VideoForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/static-elements-configs/VideoForm.vue'),
);
export const RankDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/RankDecisionForm.vue'),
);
export const AssignmentDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/AssignmentDecisionForm.vue'),
);
export const MapDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/MapDecisionForm.vue'),
);
export const RankTableDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/RankTableDecisionForm.vue'),
);
export const ConversationDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/ConversationDecisionForm.vue'),
);
export const AIConversationDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/AIConversationDecisionForm.vue'),
);
export const SelectWithStakeholderStanceDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/SelectWithStakeholderStanceDecisionForm.vue'),
);
export const ResourceAllocationDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/ResourceAllocationDecisionForm.vue'),
);
export const NumericDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/NumericDecisionForm.vue'),
);
export const SelectStakeholderMessageDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/SelectStakeholderMessageDecisionForm.vue'),
);
export const QuizDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/QuizDecisionForm.vue'),
);
export const ReportDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/ReportDecisionForm.vue'),
);
export const TextDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/TextDecisionForm.vue'),
);
export const CheckboxDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/CheckboxDecisionForm.vue'),
);
export const MultipleChoiceDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/MultipleChoiceDecisionForm.vue'),
);
export const TableDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/TableDecisionForm.vue'),
);
export const CodeDecisionForm = defineAsyncComponent(
    () => import('@/editor/core/components/forms/decision-configs/CodeDecisionForm.vue'),
);
